import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { financeJournalAPI } from "../../../../services";
import { useDebounce } from "../../../../hooks/useDebounce";
import "../../../../style/journalView.css";

import DeleteIcon from "../../../../assets/delete-modal-icon.svg";
import JournalIcon from "../../../../assets/icons/JournalIcon.js";
import FilterIcon from "../../../../assets/icons/FilterIcon.js";
import NoDataIcon from "../../../../assets/icons/NoDataIcon.js";
import JournalEditModal from "./JournalEditModal.js";
import ArrowDownUpIcon from "../../../../assets/icons/ArrowDownUpIcon.js";

import SaleIcon from "../../../../assets/icons/SaleIcon.js";
import TrendUp from "../../../../assets/icons/TrendUp.js";
import TrendDown from "../../../../assets/icons/TrendDown.js";
import CreditIcon from "../../../../assets/icons/CreditIcon.js";
import DebitIcon from "../../../../assets/icons/DebitIcon.js";
import ReturnIcon from "../../../../assets/icons/ReturnIcon.js";

import TotalCerditIcon from "../../../../assets/total-cerdit.svg";
import TotalDebitIcon from "../../../../assets/total-debit.svg";
import BalanceIcon from "../../../../assets/balance-icon.svg";

import JournalView from "./JournalView.js";
import { useNavigate } from "react-router-dom";

import { Calendar, ChevronDown, Search, Check, Download } from "lucide-react";
import * as XLSX from "xlsx";

import FilePdf from "../../../../assets/file-pdf.svg";
import FileExcel from "../../../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../../../assets/file-csv.svg";
import { useSelector } from "react-redux";

function DailyReportView() {
  const navigate = useNavigate();

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [balanceFilter, setBalanceFilter] = useState("all");
  const filterDropdownRef = useRef(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [transactionsList, setTransactionsList] = useState([]);
  const [showJournalView, setJournalView] = useState(false);
  const [countMetrics, setCountMetrics] = useState({
    credit: {
      today: 0,
      total: 0,
      count: 0,
      statistics: {
        yesterday: 0,
        today: 0,
        change: 0,
        change_percent: 0,
        trend: "stable",
      },
    },
    debit: {
      today: 0,
      total: 0,
      count: 0,
      statistics: {
        yesterday: 0,
        today: 0,
        change: 0,
        change_percent: 0,
        trend: "stable",
      },
    },
    balance: {
      today: 0,
      total: 0,
      count: 0,
      statistics: {
        yesterday: 0,
        today: 0,
        change: 0,
        change_percent: 0,
        trend: "stable",
      },
    },
  });
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const isFirstLoad = useRef(true);
  
  // Sorting state: null = original, 'asc' = ascending, 'desc' = descending
  const [balanceSort, setBalanceSort] = useState(null);
  const countMetricsCalled = useRef(false);
  const countMetricsFetching = useRef(false);
  
  // AbortController to cancel ongoing API requests
  const abortControllerRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  // Debounce search term to prevent excessive API calls (increased to 800ms for better batching)
  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  // Sample customers data
  const customers = [
    { id: "1", name: "Hussain Mansuri" },
    { id: "2", name: "Himesh Karim" },
    { id: "3", name: "Ahmed Ali" },
    { id: "4", name: "Sarah Johnson" },
    { id: "5", name: "Michael Brown" },
  ];

  // Fetch count metrics from API
  const fetchCountMetrics = useCallback(async (date = null) => {
    // Prevent duplicate simultaneous calls
    if (countMetricsFetching.current) {
      return;
    }
    countMetricsFetching.current = true;
    try {
      // Use selectedDate if no date is provided, format it for API
      const dateToUse =
        date || (selectedDate ? formatDateForAPI(selectedDate) : null);
      const response = await financeJournalAPI.getJournalMetrics(dateToUse);

      if (response && response.data) {
        setCountMetrics({
          credit: response.data.credit || {
            today: 0,
            total: 0,
            count: 0,
            statistics: {
              yesterday: 0,
              today: 0,
              change: 0,
              change_percent: 0,
              trend: "stable",
            },
          },
          debit: response.data.debit || {
            today: 0,
            total: 0,
            count: 0,
            statistics: {
              yesterday: 0,
              today: 0,
              change: 0,
              change_percent: 0,
              trend: "stable",
            },
          },
          balance: response.data.balance || {
            today: 0,
            total: 0,
            count: 0,
            statistics: {
              yesterday: 0,
              today: 0,
              change: 0,
              change_percent: 0,
              trend: "stable",
            },
          },
        });
      }
    } catch (error) {
      console.error("Error fetching count metrics:", error);
      toast.error("Failed to fetch transaction counts");
    } finally {
      countMetricsFetching.current = false;
    }
  }, [selectedDate]);

  // Fetch journals data from API
  const fetchJournalsData = useCallback(async (params = {}, abortSignal = null) => {
    setJournalsLoading(true);
    
    try {
      const response = await financeJournalAPI.getDailyReportJournals(params, abortSignal);

      if (response && response.data) {
        // Transform API data to match component structure
        const transformedData = response.data.map((journal, index) => ({
          id: journal.tnx_id || `TRN_${index + 1}`,
          customer: journal.customer_name || "Unknown Customer",
          note: journal.note || "No note provided",
          credit:
            journal.tnx_type === "credit" ? parseFloat(journal.amount) || 0 : 0,
          debit:
            journal.tnx_type === "debit" ? parseFloat(journal.amount) || 0 : 0,
          balance: parseFloat(journal.balance) || 0,
          type: journal.return_id
            ? "return"
            : journal.is_sales
            ? "sale"
            : journal.tnx_type || "credit",
          tnx_type: journal.tnx_type || "credit", // Preserve original tnx_type for CREDIT/DEBIT column
          date: journal.tnx_date || formatDateForAPI(new Date()),
          bill_amount: parseFloat(journal.bill_amount) || 0,
          paid_amount: parseFloat(journal.paid_amount) || 0,
          amount: parseFloat(journal.amount) || 0,
          is_sales: journal.is_sales,
          return_id: journal.return_id,
        }));

        setTransactionsList(transformedData);
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'canceled') {
        // Request was cancelled, don't show error
        return;
      }
      console.error("Error fetching journals data:", error);
      toast.error("Failed to fetch transactions data");
    } finally {
      setJournalsLoading(false);
    }
  }, []);

  // Fetch journals with search and filter parameters
  const fetchJournalsWithFilters = useCallback(async (searchTerm, filter, date) => {
    // Cancel any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    setSearchLoading(true);
    try {
      const params = {};

      // Add search parameter if search term exists (use complete debounced search term)
      if (searchTerm && searchTerm.trim().length > 0) {
        params.search = searchTerm.trim();
      }

      // Add type filter if not "all"
      if (filter !== "all") {
        params.type = filter;
      }

      // Add date if selected
      if (date) {
        const dateString = formatDateForAPI(date);
        params.from = dateString;
        params.to = dateString;
      }

      await fetchJournalsData(params, signal);
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'canceled') {
        // Request was cancelled, don't show error
        return;
      }
      console.error("Error fetching filtered journals:", error);
      toast.error("Failed to fetch filtered transactions");
    } finally {
      setSearchLoading(false);
    }
  }, [fetchJournalsData]);

  // Track previous date to prevent unnecessary API calls
  const previousDateRef = useRef(null);
  
  // Track previous search/filter values to prevent duplicate API calls
  const previousFiltersRef = useRef({ search: "", filter: "all", date: null });

  // Load count metrics and journals data on component mount (only once)
  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      // Only call count metrics once when component first loads (when Daily Report button is clicked)
      if (!countMetricsCalled.current) {
        countMetricsCalled.current = true;
        fetchCountMetrics();
      }
      // Use fetchJournalsWithFilters to ensure consistent filtering on initial load
      fetchJournalsWithFilters(debouncedSearchTerm, balanceFilter, selectedDate);
      previousDateRef.current = selectedDate
        ? formatDateForAPI(selectedDate)
        : null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Consolidated effect to handle all filter changes (search, filter, date)
  useEffect(() => {
    if (!isFirstLoad.current) {
      const currentDateStr = selectedDate ? formatDateForAPI(selectedDate) : null;
      
      // Check if filters actually changed to prevent duplicate API calls
      const filtersChanged = 
        previousFiltersRef.current.search !== debouncedSearchTerm ||
        previousFiltersRef.current.filter !== balanceFilter ||
        previousFiltersRef.current.date !== currentDateStr;
      
      if (!filtersChanged) {
        return;
      }
      
      // Update previous filters
      previousFiltersRef.current = {
        search: debouncedSearchTerm,
        filter: balanceFilter,
        date: currentDateStr
      };
      
      // Check if date changed to fetch count metrics
      if (previousDateRef.current !== currentDateStr) {
        previousDateRef.current = currentDateStr;
        fetchCountMetrics();
      }
      
      // Fetch journals with all current filters - pass current values directly
      fetchJournalsWithFilters(debouncedSearchTerm, balanceFilter, selectedDate);
    }
  }, [debouncedSearchTerm, balanceFilter, selectedDate, fetchCountMetrics, fetchJournalsWithFilters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isCustomerDropdownOpen &&
        !event.target.closest(".journal-customer-dropdown-container")
      ) {
        setIsCustomerDropdownOpen(false);
      }
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDatePicker(false);
      }
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
    };

    if (
      isCustomerDropdownOpen ||
      showDatePicker ||
      showFilterDropdown ||
      showExportDropdown
    ) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    isCustomerDropdownOpen,
    showDatePicker,
    showFilterDropdown,
    showExportDropdown,
  ]);

  // Date picker functions
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return date > today;
  };

  const isDateSelected = (date) => {
    if (!selectedDate) return false;
    return date.getTime() === selectedDate.getTime();
  };

  const handleDateClick = (date) => {
    if (isDateDisabled(date)) return;
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatSelectedDate = () => {
    if (selectedDate) {
      return selectedDate.toLocaleDateString("en-GB");
    }
    return "Select date";
  };

  // Filter options and functions
  const filterOptions = [
    { value: "all", label: "All" },
    { value: "credit", label: "Credit" },
    { value: "debit", label: "Debit" },
    { value: "sales", label: "Sales" },
  ];

  const handleFilterSelect = (value) => {
    setBalanceFilter(value);
    setShowFilterDropdown(false);
    // The useEffect will handle the API call
  };

  const getFilterLabel = () => {
    const option = filterOptions.find((opt) => opt.value === balanceFilter);
    return option ? option.label : "All";
  };

  const handleDeleteTransaction = (transactionId) => {
    setTransactionsList((prev) =>
      prev.filter((transaction) => transaction.id !== transactionId)
    );
    setShowDeleteModal(false);
    setDeletingTransaction(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingTransaction(null);
  };

  const handleEditTransaction = (updatedTransaction) => {
    setTransactionsList((prev) =>
      prev.map((transaction) =>
        transaction.id === updatedTransaction.id
          ? updatedTransaction
          : transaction
      )
    );
    setShowEditModal(false);
    setEditingTransaction(null);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingTransaction(null);
  };

  // Sorting toggle function: null -> 'asc' -> 'desc' -> null
  const handleBalanceSort = () => {
    if (balanceSort === null) {
      setBalanceSort('asc');
    } else if (balanceSort === 'asc') {
      setBalanceSort('desc');
    } else {
      setBalanceSort(null);
    }
  };

  // Apply sorting if active
  const getSortedTransactions = () => {
    let sorted = [...transactionsList];
    
    if (balanceSort) {
      sorted.sort((a, b) => {
        const balanceA = parseFloat(a.balance) || 0;
        const balanceB = parseFloat(b.balance) || 0;
        if (balanceSort === 'asc') {
          return balanceA - balanceB;
        } else {
          return balanceB - balanceA;
        }
      });
    }
    
    return sorted;
  };

  // Since we're now using API filtering, we can use transactionsList directly
  const filteredTransactions = getSortedTransactions();

  // Apply pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(
    startIndex,
    endIndex
  );

  // Export options and functions
  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "excel", label: "Excel", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  const handleExportSelect = (value) => {
    setShowExportDropdown(false);

    switch (value) {
      case "pdf":
        // Navigate to print page with transaction data
        navigate("/finance/journal/daily-report/print", {
          state: {
            transactions: filteredTransactions,
            selectedDate: selectedDate,
            countMetrics: countMetrics,
          },
        });
        break;
      case "excel":
        exportToExcel();
        break;
      case "csv":
        exportToCSV();
        break;
      default:
    }
  };

  const exportToExcel = () => {
    // Filter transactions for selected date only
    const selectedDateStr = selectedDate
      ? formatDateForAPI(selectedDate)
      : null;
    const dateFilteredTransactions = selectedDateStr
      ? filteredTransactions.filter(
          (transaction) => transaction.date === selectedDateStr
        )
      : filteredTransactions;

    // Prepare data for Excel
    const excelData = dateFilteredTransactions.map((transaction) => ({
      "Transaction ID": transaction.id,
      Customer: transaction.customer,
      Note: transaction.note,
      Credit: transaction.credit || 0,
      Debit: transaction.debit || 0,
      Balance: transaction.balance,
      Date: transaction.date,
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");

    // Set column widths
    ws["!cols"] = [
      { wch: 15 }, // Transaction ID
      { wch: 20 }, // Customer
      { wch: 30 }, // Note
      { wch: 12 }, // Credit
      { wch: 12 }, // Debit
      { wch: 12 }, // Balance
      { wch: 15 }, // Date
    ];

    // Save the Excel file
    XLSX.writeFile(wb, "daily-report-transactions.xlsx");
  };

  const exportToCSV = () => {
    // Filter transactions for selected date only
    const selectedDateStr = selectedDate
      ? formatDateForAPI(selectedDate)
      : null;
    const dateFilteredTransactions = selectedDateStr
      ? filteredTransactions.filter(
          (transaction) => transaction.date === selectedDateStr
        )
      : filteredTransactions;

    // Prepare CSV data
    const csvHeaders = [
      "Transaction ID",
      "Customer",
      "Note",
      "Credit",
      "Debit",
      "Balance",
      "Date",
    ];
    const csvData = dateFilteredTransactions.map((transaction) => [
      transaction.id,
      transaction.customer,
      transaction.note,
      transaction.credit || 0,
      transaction.debit || 0,
      transaction.balance,
      transaction.date,
    ]);

    // Combine headers and data
    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "daily-report-transactions.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {showJournalView ? (
        <JournalView />
      ) : (
        <div className="journal-container">
          {/* Transaction Form */}
          <div className="transaction-form">
            {/* Header */}
            <div className="journal-header">
              <div className="journal-title-section">
                <h1 className="journal-title">Report</h1>
                <p className="journal-subtitle">
                  Manage transactions and track financial data
                </p>
              </div>
              <button
                className={`${
                  isPartsURL
                    ? "daily-report-btn-parts"
                    : isAccessoriesURL
                    ? "daily-report-btn-acc"
                    : "daily-report-btn"
                }`}
                onClick={() => setJournalView(true)}
              >
                <JournalIcon
                  fill={`${
                    isPartsURL
                      ? "var(--primary-color-parts)"
                      : isAccessoriesURL
                      ? "var(--primary-color-acc)"
                      : "var(--primary-color-mobile)"
                  }`}
                />
                Journal
              </button>
            </div>

            {/* Date picker */}
            <div className="form-row">
              <div className="form-group">
                <label className="journal-view-form-label">Date</label>
                <div className="journal-date-range" ref={datePickerRef}>
                  <Calendar size={16} className="journal-date-icon" />
                  <input
                    type="text"
                    className={`${
                      isPartsURL
                        ? "journal-date-input-parts"
                        : isAccessoriesURL
                        ? "journal-date-input-acc"
                        : "journal-date-input"
                    }`}
                    value={formatSelectedDate()}
                    readOnly
                    onClick={() => setShowDatePicker(!showDatePicker)}
                  />

                  {/* Date Picker Popup */}
                  {showDatePicker && (
                    <>
                      <div
                        className="journal-date-picker-backdrop"
                        onClick={() => setShowDatePicker(false)}
                      ></div>
                      <div className="journal-date-picker">
                        <div className="journal-date-picker-header">
                          <button
                            className="journal-date-picker-nav-btn"
                            onClick={() => navigateMonth(-1)}
                          >
                            <ChevronDown
                              size={16}
                              style={{ transform: "rotate(90deg)" }}
                            />
                          </button>
                          <h3 className="journal-date-picker-month-year">
                            {currentMonth.toLocaleDateString("en-US", {
                              month: "long",
                              year: "numeric",
                            })}
                          </h3>
                          <button
                            className="journal-date-picker-nav-btn"
                            onClick={() => navigateMonth(1)}
                          >
                            <ChevronDown
                              size={16}
                              style={{ transform: "rotate(-90deg)" }}
                            />
                          </button>
                        </div>

                        <div className="journal-date-picker-weekdays">
                          <div className="journal-weekday">Mo</div>
                          <div className="journal-weekday">Tu</div>
                          <div className="journal-weekday">We</div>
                          <div className="journal-weekday">Th</div>
                          <div className="journal-weekday">Fr</div>
                          <div className="journal-weekday">Sa</div>
                          <div className="journal-weekday">Su</div>
                        </div>

                        <div className="journal-date-picker-days">
                          {getDaysInMonth(currentMonth).map((day, index) => (
                            <div
                              key={index}
                              className={` ${
                                isPartsURL
                                  ? "journal-date-picker-day-parts"
                                  : isAccessoriesURL
                                  ? "journal-date-picker-day-acc"
                                  : "journal-date-picker-day"
                              } ${day ? "" : "empty"} ${
                                day && isDateDisabled(day) ? "disabled" : ""
                              } ${
                                day && isDateSelected(day) ? "selected" : ""
                              }`}
                              onClick={() => day && handleDateClick(day)}
                            >
                              {day ? day.getDate() : ""}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="row">
              <div className="col-md-3">
                <div
                  className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                  style={{
                    border: "1px solid #EAEEF2",
                    borderRadius: "10px",
                    height: "100%",
                  }}
                >
                  <div className="d-flex flex-column align-items-start justify-content-center">
                    <div>
                      <small className="DailyReportView-text">
                        Total Credit
                      </small>
                      <h5 className="DailyReportView-value">
                        €{countMetrics.credit.today.toFixed(2)}
                      </h5>
                    </div>
                    <div className="d-flex flex-row gap-1">
                      <SaleIcon />
                      <h5 className="DailyReportView-SaleIcon-value">
                        €{countMetrics.credit.total.toFixed(2)}
                      </h5>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center gap-1 mt-2"
                      style={{ width: "120%" }}
                    >
                      <div
                        className={`d-flex flex-row gap-1 align-items-center ${
                          countMetrics.credit.statistics.change_percent >= 0
                            ? "DailyReportView-Sale-badge-green"
                            : "DailyReportView-Sale-badge-red"
                        }`}
                      >
                        {countMetrics.credit.statistics.change_percent >= 0 ? (
                          <TrendUp />
                        ) : (
                          <TrendDown />
                        )}
                        <h5 className="mt-2">
                          {countMetrics.credit.statistics.change_percent >= 0
                            ? "+"
                            : ""}
                          {countMetrics.credit.statistics.change_percent.toFixed(
                            2
                          )}
                          %
                        </h5>
                      </div>
                      <h5 className="DailyReportView-SaleIcon-text mt-1">
                        From yesterday
                      </h5>
                    </div>
                  </div>

                  <img src={TotalCerditIcon} alt="Total Credit" />
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                  style={{
                    border: "1px solid #EAEEF2",
                    borderRadius: "10px",
                    height: "100%",
                  }}
                >
                  <div className="d-flex flex-column align-items-start justify-content-center">
                    <div>
                      <small className="DailyReportView-text">
                        Total Debit
                      </small>
                      <h5 className="DailyReportView-value">
                        €{countMetrics.debit.today.toFixed(2)}
                      </h5>
                    </div>
                    <div className="d-flex flex-row gap-1">
                      <SaleIcon />
                      <h5 className="DailyReportView-SaleIcon-value">
                        €{countMetrics.debit.total.toFixed(2)}
                      </h5>
                    </div>
                    <div
                      className="d-flex flex-row align-items-center gap-1 mt-2"
                      style={{ width: "120%" }}
                    >
                      <div
                        className={`d-flex flex-row gap-1 align-items-center ${
                          countMetrics.debit.statistics.change_percent >= 0
                            ? "DailyReportView-Sale-badge-green"
                            : "DailyReportView-Sale-badge-red"
                        }`}
                      >
                        {countMetrics.debit.statistics.change_percent >= 0 ? (
                          <TrendUp />
                        ) : (
                          <TrendDown />
                        )}
                        <h5 className="mt-2">
                          {countMetrics.debit.statistics.change_percent >= 0
                            ? "+"
                            : ""}
                          {countMetrics.debit.statistics.change_percent.toFixed(
                            2
                          )}
                          %
                        </h5>
                      </div>
                      <h5 className="DailyReportView-SaleIcon-text mt-1">
                        From yesterday
                      </h5>
                    </div>
                  </div>

                  <img src={TotalDebitIcon} alt="Total Debit" />
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="p-3 d-flex align-items-start justify-content-between daily-report-card-shadow"
                  style={{
                    border: "1px solid #EAEEF2",
                    borderRadius: "10px",
                    height: "100%",
                  }}
                >
                  <div className="d-flex flex-column align-items-start justify-content-center">
                    <div>
                      <small className="DailyReportView-text">Balance</small>
                      <h5 className="DailyReportView-value">
                        €{countMetrics.balance.today.toFixed(2)}
                      </h5>
                    </div>
                    {/* <div className="d-flex flex-row gap-1">
                      <SaleIcon/>
                      <h5 className="DailyReportView-SaleIcon-value">
                        €{countMetrics.balance.total.toFixed(2)}
                      </h5>
                    </div> */}
                  </div>

                  <img src={BalanceIcon} alt="Total Balance" />
                </div>
              </div>
            </div>
          </div>

          {/* Transactions Card */}
          <div className="transactions-section">
            <div className="transactions-header">
              <h2 className="transactions-title">All Transaction</h2>

              <div className="transactions-controls">
                <div
                  className={`${
                    isPartsURL
                      ? "search-wrapper-parts"
                      : isAccessoriesURL
                      ? "search-wrapper-acc"
                      : "search-wrapper"
                  }`}
                >
                  <Search className="search-icon" size={16} />
                  <input
                    type="text"
                    className={`${
                      isPartsURL
                        ? "transactions-search-parts"
                        : isAccessoriesURL
                        ? "transactions-search-acc"
                        : "transactions-search"
                    }`}
                    placeholder="Search by customer name or amount..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div
                  className="journal-filter-dropdown"
                  ref={filterDropdownRef}
                >
                  <button
                    className={`${
                      isPartsURL
                        ? "journal-filter-button-parts"
                        : isAccessoriesURL
                        ? "journal-filter-button-acc"
                        : "journal-filter-button"
                    }`}
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FilterIcon size={16} />
                      {getFilterLabel()}
                    </div>

                    <ChevronDown size={16} />
                  </button>

                  <div
                    className={`journal-filter-dropdown-content ${
                      showFilterDropdown ? "show" : ""
                    }`}
                  >
                    {filterOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`journal-filter-dropdown-item ${
                          balanceFilter === option.value ? "selected" : ""
                        }`}
                        onClick={() => handleFilterSelect(option.value)}
                      >
                        <Check
                          size={16}
                          className="journal-filter-dropdown-check"
                        />
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Export Dropdown */}
                <div
                  className="journal-export-dropdown"
                  ref={exportDropdownRef}
                >
                  <button
                    className={` ${
                      isPartsURL
                        ? "journal-export-button-parts"
                        : isAccessoriesURL
                        ? "journal-export-button-acc"
                        : "journal-export-button"
                    }`}
                    onClick={() => setShowExportDropdown(!showExportDropdown)}
                  >
                    <Download size={16} />
                    Export
                    <ChevronDown
                      size={12}
                      className={`journal-export-dropdown-arrow ${
                        showExportDropdown ? "rotated" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`journal-export-dropdown-content ${
                      showExportDropdown ? "show" : ""
                    }`}
                  >
                    {exportOptions.map((option) => (
                      <div
                        key={option.value}
                        className="journal-export-dropdown-item"
                        onClick={() => handleExportSelect(option.value)}
                      >
                        <img
                          src={option.icon}
                          alt={option.label}
                          width="16"
                          height="16"
                        />
                        {option.label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Finance History Table */}
            <div className="journal-table-container">
              <div className="journal-table-responsive">
                <table className="journal-table">
                  <thead>
                    <tr>
                      <th>INV/TRN ID</th>
                      <th>CUSTOMER</th>
                      <th style={{ width: "10%" }}>TYPE</th>
                      <th style={{ width: "25%" }}>DETAILS</th>
                      <th>CREDIT / DEBIT</th>
                      <th
                        onClick={handleBalanceSort}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        title={balanceSort === null ? 'Click to sort ascending' : balanceSort === 'asc' ? 'Click to sort descending' : 'Click to reset'}
                      >
                        BALANCE <ArrowDownUpIcon color={balanceSort ? isPartsURL ? 'var(--primary-color-parts)' : isAccessoriesURL ? 'var(--primary-color-acc)' : 'var(--primary-color-mobile)' : '#57606A'} sortDirection={balanceSort} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {journalsLoading || searchLoading ? (
                      <tr>
                        <td colSpan="6" className="no-products-message">
                          <div
                            className=""
                            style={{
                              marginBottom: "4rem",
                              marginTop: "4rem",
                            }}
                          >
                            <div
                              className="spinner-border text-primary"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                            <h3 style={{ marginTop: "2rem" }}>
                              {searchLoading
                                ? "Searching transactions..."
                                : "Loading transactions..."}
                            </h3>
                          </div>
                        </td>
                      </tr>
                    ) : paginatedTransactions.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-products-message">
                          <div
                            className=""
                            style={{
                              marginBottom: "4rem",
                              marginTop: "4rem",
                            }}
                          >
                            <NoDataIcon />
                            <h3 style={{ marginTop: "2rem" }}>
                              {searchTerm || balanceFilter !== "all"
                                ? "No transactions match your search criteria"
                                : "No transactions found"}
                            </h3>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedTransactions.map((transaction, index) => (
                        <tr key={transaction.id}>
                          <td>{transaction.id}</td>
                          <td
                            className="journal-customer-name"
                            title={transaction.customer}
                          >
                            {transaction.customer}
                          </td>
                          <td style={{ width: "10%" }}>
                            <div className="journal-transaction-type">
                              {transaction.type === "credit" && (
                                <>
                                  <CreditIcon />
                                  <span>Credit</span>
                                </>
                              )}
                              {transaction.type === "debit" && (
                                <>
                                  <DebitIcon />
                                  <span>Debit</span>
                                </>
                              )}
                              {transaction.type === "sale" && (
                                <>
                                  <SaleIcon />
                                  <span>Sale</span>
                                </>
                              )}
                              {transaction.type === "return" && (
                                <>
                                  <ReturnIcon />
                                  <span>Return</span>
                                </>
                              )}
                            </div>
                          </td>
                          <td
                            style={{ width: "25%" }}
                            title={
                              transaction.type === "sale"
                                ? `Bill: €${transaction.bill_amount.toFixed(
                                    2
                                  )}, Paid: €${transaction.paid_amount.toFixed(
                                    2
                                  )}`
                                : transaction.type === "return"
                                ? `Return Device: €${transaction.bill_amount.toFixed(
                                    2
                                  )}, Exchanged Device: €${transaction.paid_amount.toFixed(
                                    2
                                  )}`
                                : transaction.note
                            }
                          >
                            {transaction.type === "sale" ? (
                              <div className="d-flex gap-3 flex-row ">
                                <div className="d-flex flex-column">
                                  <p className="text-muted">Bill Amount</p>
                                  <div className="customer-details-amount">
                                    €{transaction.bill_amount.toFixed(2)}
                                  </div>
                                </div>

                                <div className="d-flex flex-column">
                                  <p className="text-muted">Paid</p>
                                  <div className="customer-details-amount positive">
                                    €{transaction.paid_amount.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            ) : transaction.type === "return" ? (
                              <div className="d-flex flex-column">
                                <div
                                  className="d-flex flex-row justify-content-between"
                                  style={{ marginBottom: "-5px" }}
                                >
                                  <p className="text-muted">Return Device</p>
                                  <div className="customer-details-amount">
                                    €{transaction.bill_amount.toFixed(2)}
                                  </div>
                                </div>

                                <div className="d-flex flex-row justify-content-between ">
                                  <p className="text-muted">Exchanged Device</p>
                                  <div
                                    className="customer-details-amount positive"
                                    style={{ color: "#D800B0" }}
                                  >
                                    €{transaction.paid_amount.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <td
                                className="journal-note"
                                title={transaction.note}
                              >
                                {transaction.note}
                              </td>
                            )}
                          </td>
                          <td>
                            {transaction.tnx_type === "credit" ? (
                              <div className="journal-amount negative">
                                - €{transaction.amount.toFixed(2)}
                              </div>
                            ) : (
                              <div className="journal-amount positive">
                                + €{transaction.amount.toFixed(2)}
                              </div>
                            )}
                          </td>
                          <td>
                            <div
                              className={`balance-badge ${
                                transaction.balance < 0
                                  ? "negative"
                                  : "positive"
                              }`}
                            >
                              €{transaction.balance.toFixed(2)}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {filteredTransactions.length > itemsPerPage && (
              <div className="pagination-wrapper">
                <span className="pagination-info">
                  Page {currentPage} of{" "}
                  {Math.ceil(filteredTransactions.length / itemsPerPage)}
                </span>
                <div className="pagination-controls">
                  <button
                    className={`${
                      isPartsURL
                        ? "pagination-btn-parts"
                        : isAccessoriesURL
                        ? "pagination-btn-acc"
                        : "pagination-btn"
                    }`}
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                  >
                    Previous
                  </button>
                  <button
                    className={`${
                      isPartsURL
                        ? "pagination-btn-next-parts"
                        : isAccessoriesURL
                        ? "pagination-btn-next-acc"
                        : "pagination-btn-next"
                    }`}
                    disabled={
                      currentPage >=
                      Math.ceil(filteredTransactions.length / itemsPerPage)
                    }
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(
                          Math.ceil(filteredTransactions.length / itemsPerPage),
                          prev + 1
                        )
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}

            {/* Edit Modal */}
            {showEditModal && editingTransaction && (
              <JournalEditModal
                isOpen={showEditModal}
                onClose={handleCancelEdit}
                transaction={editingTransaction}
                customers={customers}
                onSave={handleEditTransaction}
              />
            )}

            {/* Delete Modal */}
            {showDeleteModal && deletingTransaction && (
              <>
                <div className="modal-overlay">
                  <div className="delete-modal">
                    <div className="modal-icon">
                      <img src={DeleteIcon} alt="Delete" />
                    </div>
                    <h3 className="modal-title">Delete Transaction</h3>
                    <p className="modal-message">
                      Are you sure you want to delete transaction{" "}
                      <b>{deletingTransaction.id}</b>?
                      <br />
                      This action cannot be undone.
                    </p>
                    <div className="modal-actions">
                      <button
                        className="modal-cancel-btn"
                        onClick={handleCancelDelete}
                      >
                        Cancel
                      </button>
                      <button
                        className="modal-delete-btn"
                        onClick={() =>
                          handleDeleteTransaction(deletingTransaction.id)
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default DailyReportView;
